import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEOMeta from '../components/SEOMeta';

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Header = styled.header`
  background: white;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const Content = styled.div`
  padding: 24px 16px;
  background: white;
  min-height: calc(100dvh - 60px);
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Paragraph = styled.p`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 12px;
`;

const List = styled.ul`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 12px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 6px;
`;

const UpdateDate = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 24px;
`;

const ContactBox = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin-top: 24px;
`;

const ContactTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  margin-bottom: 8px;
`;

const ContactEmail = styled.a`
  font-size: 14px;
  color: #2DBCAF;
  text-decoration: none;
`;

const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <SEOMeta 
        title="Syarat & Ketentuan | KinderGame.id"
        description="Syarat dan ketentuan penggunaan Kindergame"
      />
      
      <Header>
        <BackButton onClick={() => navigate(-1)}>←</BackButton>
        <HeaderTitle>Syarat & Ketentuan</HeaderTitle>
      </Header>

      <Content>
        <UpdateDate>Terakhir diperbarui: Maret 2026</UpdateDate>

        <Section>
          <SectionTitle>1. Penerimaan Ketentuan</SectionTitle>
          <Paragraph>
            Dengan mengakses dan menggunakan Kindergame ("Layanan"), Anda menyetujui 
            untuk terikat dengan Syarat & Ketentuan ini. Jika Anda tidak menyetujui 
            ketentuan ini, mohon untuk tidak menggunakan Layanan kami.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Definisi</SectionTitle>
          <List>
            <ListItem><strong>"Kindergame"</strong> atau <strong>"Kami"</strong>: PT Dewangga Bersaudara Jaya Bersama, penyedia platform Kindergame</ListItem>
            <ListItem><strong>"Pengguna"</strong> atau <strong>"Anda"</strong>: Setiap individu yang mengakses atau menggunakan Layanan</ListItem>
            <ListItem><strong>"Anak"</strong>: Pengguna di bawah usia 18 tahun yang menggunakan Layanan di bawah pengawasan orang tua/wali</ListItem>
            <ListItem><strong>"Layanan"</strong>: Platform, website, aplikasi, dan seluruh fitur Kindergame</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Persyaratan Usia</SectionTitle>
          <List>
            <ListItem>Pendaftaran akun hanya dapat dilakukan oleh orang tua/wali berusia minimal 18 tahun</ListItem>
            <ListItem>Anak-anak di bawah 13 tahun wajib didampingi atau mendapat izin orang tua/wali</ListItem>
            <ListItem>Orang tua/wali bertanggung jawab penuh atas aktivitas anak dalam menggunakan Layanan</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Akun Pengguna</SectionTitle>
          <List>
            <ListItem>Anda bertanggung jawab menjaga kerahasiaan informasi akun</ListItem>
            <ListItem>Satu akun hanya untuk satu keluarga (dapat digunakan beberapa anak dalam satu rumah tangga)</ListItem>
            <ListItem>Dilarang membagikan akses akun kepada pihak lain di luar keluarga</ListItem>
            <ListItem>Kami berhak menangguhkan akun yang melanggar ketentuan</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>5. Penggunaan yang Dilarang</SectionTitle>
          <Paragraph>Pengguna dilarang untuk:</Paragraph>
          <List>
            <ListItem>Menyalin, memodifikasi, atau mendistribusikan konten Kindergame</ListItem>
            <ListItem>Menggunakan Layanan untuk tujuan ilegal</ListItem>
            <ListItem>Mencoba mengakses sistem secara tidak sah (hacking)</ListItem>
            <ListItem>Membuat akun palsu atau menyalahgunakan sistem refund</ListItem>
            <ListItem>Menggunakan bot atau alat otomatis</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Hak Kekayaan Intelektual</SectionTitle>
          <Paragraph>
            Seluruh konten dalam Kindergame termasuk namun tidak terbatas pada desain, 
            grafis, animasi, musik, dan kode program adalah milik Kindergame dan dilindungi 
            oleh hukum hak cipta Indonesia. Pengguna tidak diizinkan untuk mereproduksi, 
            mendistribusikan, atau membuat karya turunan tanpa izin tertulis.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Pembayaran dan Langganan</SectionTitle>
          <List>
            <ListItem>Harga yang tercantum sudah termasuk pajak yang berlaku</ListItem>
            <ListItem>Pembayaran diproses melalui payment gateway pihak ketiga yang aman</ListItem>
            <ListItem>Langganan berlaku sesuai periode yang dipilih</ListItem>
            <ListItem>Ketentuan refund mengacu pada Kebijakan Pengembalian Dana</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>8. Pembatasan Tanggung Jawab</SectionTitle>
          <Paragraph>Kindergame tidak bertanggung jawab atas:</Paragraph>
          <List>
            <ListItem>Kerugian akibat gangguan internet atau perangkat pengguna</ListItem>
            <ListItem>Kerusakan yang timbul dari penggunaan Layanan di luar ketentuan</ListItem>
            <ListItem>Konten pihak ketiga yang mungkin diakses melalui link eksternal</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>9. Perubahan Layanan dan Ketentuan</SectionTitle>
          <Paragraph>Kami berhak untuk:</Paragraph>
          <List>
            <ListItem>Mengubah, menangguhkan, atau menghentikan fitur Layanan</ListItem>
            <ListItem>Memperbarui Syarat & Ketentuan ini sewaktu-waktu</ListItem>
            <ListItem>Perubahan akan diberitahukan melalui email atau notifikasi dalam aplikasi</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>10. Hukum yang Berlaku</SectionTitle>
          <Paragraph>
            Syarat & Ketentuan ini diatur oleh dan ditafsirkan sesuai dengan hukum 
            Republik Indonesia. Setiap sengketa akan diselesaikan melalui musyawarah, 
            dan jika tidak tercapai, melalui Pengadilan Negeri di wilayah hukum yang berwenang.
          </Paragraph>
        </Section>

        <ContactBox>
          <ContactTitle>📧 Kontak</ContactTitle>
          <Paragraph style={{ margin: 0 }}>
            Untuk pertanyaan mengenai Syarat & Ketentuan ini, hubungi kami di:
          </Paragraph>
          <ContactEmail href="mailto:legal@kindergame.id">
            legal@kindergame.id
          </ContactEmail>
        </ContactBox>
      </Content>
    </PageWrapper>
  );
};

export default TermsPage;
